import React from "react";
import { Link } from "react-router-dom";
import { Mail, LifeBuoy, CalendarClock, MessageSquare, CreditCard, ShieldCheck } from "lucide-react";

const SUPPORT_EMAIL = "thenerdydermy@icloud.com";

const TOPICS = [
  { icon: CalendarClock, h: "Bookings & calendar", p: "Creating, rescheduling and cancelling appointments, deposits, and online booking for your clients." },
  { icon: MessageSquare, h: "Messages & reminders", p: "Setting up automated confirmations, reminders, rebook nudges and aftercare, plus SMS sender setup." },
  { icon: CreditCard, h: "Billing & payments", p: "Your Azordi subscription, in-app payments and deposits via Stripe, and SMS credit top-ups." },
  { icon: ShieldCheck, h: "Your data & privacy", p: "How client records are stored and protected. See our Privacy Policy for the full detail." },
];

export default function Support() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="support-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="support-home-link">
            <img src="/azordi-logo.webp" alt="azordi" className="h-8 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Back to home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center gap-3">
          <LifeBuoy className="h-8 w-8 text-primary" />
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Support</h1>
        </div>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          We're here to help you get the most out of Azordi. Email us any time and we'll get back to you as soon as we can — usually within 1 business day.
        </p>

        <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-link"
           className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-3 font-medium hover:opacity-90 transition-opacity">
          <Mail className="h-4 w-4" /> Email support · {SUPPORT_EMAIL}
        </a>

        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          {TOPICS.map((t) => (
            <div key={t.h} className="rounded-2xl border border-border p-5" data-testid={`support-topic-${t.h.split(" ")[0].toLowerCase()}`}>
              <t.icon className="h-5 w-5 text-primary" />
              <h2 className="font-heading font-semibold text-base mt-3">{t.h}</h2>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{t.p}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-border p-6">
          <h2 className="font-heading font-semibold text-lg">Still need a hand?</h2>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Reach us via our <Link to="/contact" className="text-primary hover:underline">contact page</Link>, or email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>. Please include your clinic name and a short description of what you need.
          </p>
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 text-center text-sm text-muted-foreground">
          The Nerdy Dermy · Erskine Park NSW, Australia · <Link to="/contact" className="hover:text-foreground">Contact</Link> · <Link to="/privacy" className="hover:text-foreground">Privacy</Link> · <Link to="/terms" className="hover:text-foreground">Terms</Link>
        </div>
      </footer>
    </div>
  );
}
